import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Cloud, Users, FileText, Calendar, MessageSquare, Folder } from 'lucide-react';

const Microsoft365Optimierung = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Microsoft 365 Optimierung Mönchengladbach',
    description: 'Microsoft 365 sinnvoll nutzen: Teams, SharePoint, OneDrive richtig einsetzen. Pragmatische Beratung für KMU in Mönchengladbach.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'Microsoft 365 Beratung'
  };

  const probleme = [
    'Sie zahlen monatlich für Microsoft 365, nutzen aber nur Outlook',
    'Niemand weiß so richtig, was Teams, SharePoint oder Planner eigentlich können',
    'Jeder speichert Dateien woanders – keine einheitliche Struktur',
    'E-Mail-Flut ohne Ende, keine klare Kommunikation',
    'Zusammenarbeit funktioniert, aber umständlich'
  ];

  const tools = [
    {
      icon: MessageSquare,
      name: 'Teams',
      text: 'Chat, Videokonferenzen und Dateiaustausch an einem Ort. Weniger E-Mails, schnellere Abstimmung.'
    },
    {
      icon: Folder,
      name: 'SharePoint',
      text: 'Zentrale Ablage für Dokumente. Jeder findet, was er braucht. Keine veralteten Versionen mehr.'
    },
    {
      icon: Calendar,
      name: 'Planner',
      text: 'Aufgaben und Projekte im Blick behalten. Wer macht was bis wann? Einfach und übersichtlich.'
    },
    {
      icon: Cloud,
      name: 'OneDrive',
      text: 'Persönlicher Speicher, überall verfügbar. Automatische Sicherung, kein USB-Stick mehr nötig.'
    }
  ];

  const beispiele = [
    {
      title: 'Handwerksbetrieb',
      text: 'Projektordner in SharePoint: Fotos, Pläne, Rechnungen an einem Ort. Monteure haben per Handy Zugriff.'
    },
    {
      title: 'Steuerberater',
      text: 'Teams-Channels für Mandanten. Dokumente direkt im Chat teilen, keine E-Mail-Anhänge mehr suchen.'
    },
    {
      title: 'Pflegedienst',
      text: 'Dienstpläne in Planner. Alle sehen, wer wann wo ist. Änderungen werden sofort sichtbar.'
    }
  ];

  const liefere = [
    'Bestandsaufnahme: Welche Tools haben Sie, was nutzen Sie davon?',
    'Empfehlung: Was macht für Ihr Unternehmen Sinn?',
    'Schulung: Ich zeige Ihrem Team, wie die Tools funktionieren',
    'Struktur: Wir richten Ordner, Teams und Prozesse ein',
    'Dokumentation: Einfache Anleitungen für Ihr Team'
  ];

  const relatedPages = [
    { title: 'Digitalisierung & Prozesse', url: '/digitalisierung-prozessoptimierung', description: 'Arbeitsabläufe vereinfachen' },
    { title: 'IT-Sicherheit', url: '/it-sicherheit-isms', description: 'Daten sicher speichern und teilen' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Microsoft 365 Optimierung Mönchengladbach – Beratung für KMU"
        description="Microsoft 365 sinnvoll nutzen: Teams, SharePoint, OneDrive richtig einsetzen. Pragmatische Beratung für KMU in Mönchengladbach, Viersen und Krefeld."
        keywords="Microsoft 365 Optimierung Mönchengladbach, Microsoft 365 Beratung, Teams SharePoint OneDrive, Microsoft 365 KMU, Office 365 Beratung, Digitalisierung Mönchengladbach, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Cloud className="w-4 h-4 mr-2" />
              Microsoft 365
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Microsoft 365 Optimierung in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Sie zahlen schon dafür – nutzen Sie es auch richtig. Ich zeige KMU in Mönchengladbach, Viersen und Krefeld, wie Sie mit Teams, SharePoint und Co. Zeit sparen und besser zusammenarbeiten.
            </p>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Kennen Sie das?</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {probleme.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{problem}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg text-slate-700 mt-8 font-medium">
                  Das muss nicht so bleiben.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Die wichtigsten Tools – einfach erklärt
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Microsoft 365 hat viele Funktionen. Aber Sie brauchen nicht alles. Ich zeige Ihnen, was wirklich hilft.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tools.map((tool, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                      <tool.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 pt-2">{tool.name}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{tool.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beispiele */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Beispiele aus dem KMU-Alltag
            </h2>
            <p className="text-xl text-slate-600">
              So nutzen andere Unternehmen Microsoft 365:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {beispiele.map((beispiel, index) => (
              <Card key={index} className="border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{beispiel.title}</h3>
                  <p className="text-slate-600">{beispiel.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Was Sie bekommen */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Was Sie von mir bekommen</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {liefere.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Das könnte Sie auch interessieren</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {relatedPages.map((page, index) => (
              <Link key={index} to={page.url}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                      {page.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{page.description}</p>
                    <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                      <span className="text-sm font-medium">Mehr erfahren</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Holen Sie mehr aus Microsoft 365 heraus
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns schauen, wie Sie Microsoft 365 besser nutzen können – ohne komplizierte Schulungen.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Microsoft365Optimierung;
